/**
 * A sheet's pages, one shown at a time. Going deeper, the next page slides in from the right and
 * the one before slides away to the left; going back, the other way round. The sheet's height
 * follows from one page to the other, so the planet above moves along smoothly. The pages are a
 * table, the first one first; `page` names the one to show. The sheet's back button
 * (Sheet.tsx, `back`) is the way back.
 */
import { useEffect, useLayoutEffect, useRef, useState, type JSX, type ReactNode } from 'react';

import './pages.css';

/** How long a page takes to slide in or out (pages.css). */
export const PAGE_MS = 320;

const still = (): boolean => matchMedia('(prefers-reduced-motion: reduce)').matches;

export function SheetPages<P extends string>({
  page,
  pages,
}: {
  page: P;
  pages: Readonly<Record<P, ReactNode>>;
}): JSX.Element {
  const [shown, setShown] = useState(page);
  const [leaving, setLeaving] = useState<P | null>(null);
  const [back, setBack] = useState(false);
  if (page !== shown) {
    // another page: the one shown slides away, the way it came from when going back
    const order = Object.keys(pages);
    setBack(order.indexOf(page) < order.indexOf(shown));
    setLeaving(shown);
    setShown(page);
  }
  const box = useRef<HTMLDivElement>(null);

  // the page that slid away is gone once it has
  useEffect(() => {
    if (leaving === null) return;
    const gone = setTimeout(() => setLeaving(null), still() ? 0 : PAGE_MS);
    return () => clearTimeout(gone);
  }, [leaving, shown]);

  // the height goes from the page that leaves to the one that comes, before anything is painted
  useLayoutEffect(() => {
    const el = box.current;
    const from = (el?.firstElementChild as HTMLElement | null)?.offsetHeight;
    const to = (el?.lastElementChild as HTMLElement | null)?.offsetHeight;
    if (!el || leaving === null || from === undefined || to === undefined || still()) return;
    el.style.height = `${String(from)}px`;
    el.getBoundingClientRect(); // the old height first, then the new one: a transition between them
    el.style.transition = `height ${String(PAGE_MS)}ms var(--ease)`;
    el.style.height = `${String(to)}px`;
    const done = setTimeout(() => {
      el.style.height = '';
      el.style.transition = '';
    }, PAGE_MS);
    return () => {
      clearTimeout(done);
      el.style.height = '';
      el.style.transition = '';
    };
  }, [leaving, shown]);

  return (
    <div
      ref={box}
      className={`sheet-pages${leaving === null ? '' : back ? ' going-back' : ' going-on'}`}
    >
      {leaving !== null && (
        <div key={leaving} className="sheet-page leaving" aria-hidden="true" inert>
          {pages[leaving]}
        </div>
      )}
      <div key={shown} className="sheet-page shown" data-page={shown}>
        {pages[shown]}
      </div>
    </div>
  );
}
